import { cx } from "@emotion/css";
import { endOfDay, format, startOfDay } from "date-fns";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type DeckStatsType, DeckType } from "src/api/flashcardDecks";
import { DateRangePicker } from "src/components/Default/Calendar/DateRangePicker";
import { GreyButton } from "src/components/Default/LekUI/Button";
import { DeckStatsTypeTabs } from "src/components/Modals/StatisticsModals/DeckStatsTypeTabs";
import { DefaultDeckStatisticsModal } from "src/components/Modals/StatisticsModals/DefaultDeckStatisticsModal";
import { buildDeckStatsContent } from "src/components/Modals/StatisticsModals/deckStatsContent";
import { useGetDeckStats } from "src/hooks/useFlashcards";
import CalendarIcon from "src/images/Icons/newIcons/calendar.svg";

interface DeckStatisticsModalProps {
	onClose: () => void;
	deckId: number;
	deckType: DeckStatsType;
	createdAt: string;
}

export const DeckStatisticsModal = ({
	onClose,
	deckId,
	deckType,
	createdAt,
}: DeckStatisticsModalProps) => {
	const { t } = useTranslation();
	const minDate = useMemo(() => startOfDay(new Date(createdAt)), [createdAt]);
	const [dates, setDates] = useState<[Date, Date]>(() => [
		minDate,
		endOfDay(new Date()),
	]);
	const [statsType, setStatsType] = useState<DeckType>(() =>
		deckType === DeckType.SpacedRepetition
			? DeckType.SpacedRepetition
			: DeckType.KnowDontKnow,
	);

	const dateParams = useMemo(
		() => ({
			startDate: startOfDay(dates[0]).toISOString(),
			endDate: endOfDay(dates[1]).toISOString(),
			type: statsType,
		}),
		[dates, statsType],
	);

	const { data: stats, isLoading } = useGetDeckStats(deckId, dateParams);

	const subTabs = (
		<DeckStatsTypeTabs value={statsType} onChange={setStatsType} />
	);

	const datePicker = (
		<DateRangePicker
			dates={dates}
			setDates={setDates}
			minDate={minDate}
			renderTrigger={({
				setAnchor,
				anchor,
				isDateSelected,
				selectedRange,
			}: {
				setAnchor: (e: React.MouseEvent<HTMLElement>) => void;
				anchor: HTMLElement | null;
				isDateSelected: boolean;
				selectedRange: { from: Date; to: Date };
			}) => (
				<GreyButton
					onClick={setAnchor}
					buttonRef={anchor as unknown as React.Ref<HTMLButtonElement>}
					endIcon={<CalendarIcon className="icon-normal" />}
					className="flex justify-around items-center min-w-150"
				>
					<span
						className={cx("size-14", {
							"font-bold": isDateSelected,
						})}
					>
						{isDateSelected
							? `${format(selectedRange.from, "dd.MM")} - ${format(selectedRange.to, "dd.MM")}`
							: t("datePicker.selectDate")}
					</span>
				</GreyButton>
			)}
		/>
	);

	if (!stats) {
		return null;
	}

	const { chart, statsList } = buildDeckStatsContent(statsType, stats, t);

	return (
		<DefaultDeckStatisticsModal
			onClose={onClose}
			isLoading={isLoading}
			chart={chart}
			statsList={statsList}
			primaryAction={null}
			deckId={stats.deckId}
			deckType={statsType}
			startDate={dateParams.startDate}
			endDate={dateParams.endDate}
			datePicker={datePicker}
			subTabs={subTabs}
			hidePrimaryAction
		/>
	);
};
